import React from 'react';
import {
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ActionButton, Dialog } from 'app/components';
import { TranslationKeys } from 'app/translations';
import { FormattedMessage, useIntl } from 'react-intl';
import type { BatchData, BatchDataFieldType, BatchDataFieldValue } from '../../../../../../../../../../types';
import formatter from '../../../../../../../../../../utils/formatter';
import { locale } from '../../../../../../../../../../components/Intl/IntlWrapper';

type CompareBatchAddNewEmployeeDialogProps = {
  batchId?: string;
  connectionId?: string;
  insurerData: BatchData;
  openAddNewEmployeeDialog: boolean;
  onCloseDialog: () => void;
  onConfirm: (batchData: BatchData) => void;
};

export const CompareBatchAddNewEmployeeDialog = ({
  insurerData,
  openAddNewEmployeeDialog,
  onCloseDialog,
  onConfirm,
}: CompareBatchAddNewEmployeeDialogProps) => {
  const intl = useIntl();

  const getFormattedFieldValue = React.useCallback(
    ({ type, value }: { type: BatchDataFieldType; value: BatchDataFieldValue }) => {
      if (value === null) {
        return '-';
      }
      if (type === 'percentage' && (typeof value === 'string' || typeof value === 'number')) {
        return formatter.percentageFormat({ value, locale });
      }
      if (type === 'date') {
        return formatter.date(value);
      }
      if (type === 'boolean') {
        return formatter.yesNo(value);
      }
      if (type === 'currency' && (typeof value === 'string' || typeof value === 'number')) {
        const result = formatter.currencyFormat({ value, locale });

        return result === '' ? value : result;
      }

      return value;
    },
    [],
  );

  return (
    <Dialog
      open={openAddNewEmployeeDialog}
      title={<FormattedMessage id={TranslationKeys.employees_add} />}
      onClose={() => {
        onCloseDialog();
      }}
    >
      <DialogContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {intl.formatMessage({ id: TranslationKeys.global_field })}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {intl.formatMessage({ id: TranslationKeys.global_insurer })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(insurerData.fields || []).map((field, idx) => {
              const valueTextColor = field.isDifferent ? 'error' : undefined;

              return (
                <TableRow key={`dialog_row_${idx}`}>
                  <TableCell>
                    <Typography fontWeight="bold">{intl.formatMessage({ id: field.name })}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color={valueTextColor}>
                      {getFormattedFieldValue({ type: field.type, value: field.insurerValue })}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <ActionButton messageId={TranslationKeys.button_confirm} onClick={() => onConfirm(insurerData)} />
      </DialogActions>
    </Dialog>
  );
};
